import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteMerchValue } from "@/lib/api/risk/merchValues";

interface DeleteMerchValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchValueId: string;
}

export function DeleteMerchValueDialog({
  open,
  onOpenChange,
  merchValueId,
}: DeleteMerchValueDialogProps) {
  const { toast } = useToast();
  const deleteMerchValue = useDeleteMerchValue();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      const result = await deleteMerchValue(merchValueId);

      if (result.success) {
        toast({ title: "Success", description: "Merch value deleted successfully" });
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to delete merch value",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isDeleting) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Delete Merch Value
          </DialogTitle>
          <DialogDescription className="pt-2">
            Are you sure you want to delete this merch value? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
